import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, BookOpen, ChevronRight, Eye, ThumbsUp, Tag, ArrowLeft, Sparkles } from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { GlassInput } from "@/components/ui/GlassInput";
import { NxButton } from "@/components/ui/NxButton";
import { NxBadge } from "@/components/ui/NxBadge";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet, apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

const CATEGORY_COLOR = {
    faturacao: "#C87941",
    instalacao: "#2D6A8A",
    suporte_tecnico: "#7A5A9C",
    comercial: "#2D7A4F",
    geral: "#94A3B8",
};

const KBPage = () => {
    const [articles, setArticles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [search, setSearch] = useState("");
    const [activeCategory, setActiveCategory] = useState("");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async (cat = "", q = "") => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (cat) params.set("category", cat);
            if (q) params.set("q", q);
            const [list, cats] = await Promise.all([
                apiGet(`/kb/articles${params.toString() ? "?" + params.toString() : ""}`),
                apiGet("/kb/categories"),
            ]);
            setArticles(list || []);
            setCategories(cats || []);
        } catch (e) { /* ignore */ }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    // debounce search
    useEffect(() => {
        const t = setTimeout(() => load(activeCategory, search), 250);
        return () => clearTimeout(t);
    }, [search, activeCategory]);

    const openArticle = async (id) => {
        try {
            const a = await apiGet(`/kb/articles/${id}`);
            setSelected(a);
        } catch (e) { /* ignore */ }
    };

    const markHelpful = async () => {
        if (!selected) return;
        try {
            const r = await apiPost(`/kb/articles/${selected.id}/helpful`, { helpful: true });
            setSelected({ ...selected, helpful_count: r.helpful_count });
        } catch (e) { /* ignore */ }
    };

    if (selected) {
        return (
            <div data-testid="kb-detail-page" className="space-y-6">
                <div className="flex items-center gap-3">
                    <NxButton variant="secondary" size="sm" onClick={() => setSelected(null)} icon={<ArrowLeft size={14} />} data-testid="btn-kb-back">
                        {pt.kb.backToList}
                    </NxButton>
                </div>
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <GlassCard padding="p-8">
                        <div className="flex items-center gap-2 mb-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wide" style={{ background: `${CATEGORY_COLOR[selected.category]}22`, color: CATEGORY_COLOR[selected.category] }}>
                                {pt.kb.categories[selected.category] || selected.category}
                            </span>
                            <span className="font-mono text-[11px] text-[color:var(--slate-mid)] flex items-center gap-1">
                                <Eye size={11} /> {selected.views} {pt.kb.views}
                            </span>
                        </div>
                        <h1 className="font-display text-3xl text-[color:var(--slate-deep)] tracking-tight mb-3">
                            {selected.title}
                        </h1>
                        {selected.summary && (
                            <p className="font-body text-base text-[color:var(--slate-mid)] mb-6 leading-relaxed italic">
                                {selected.summary}
                            </p>
                        )}
                        <div className="font-body text-[15px] text-[color:var(--slate-deep)] leading-[1.75] whitespace-pre-wrap" data-testid="kb-article-body">
                            {selected.body}
                        </div>
                        {selected.tags?.length > 0 && (
                            <div className="mt-6 pt-4 border-t border-[color:var(--glass-border)] flex flex-wrap gap-1.5">
                                {selected.tags.map((t, i) => (
                                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[color:var(--copper-subtle)] text-[color:var(--copper-dark)] font-mono text-[11px]">
                                        <Tag size={10} />{t}
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="mt-6 flex items-center gap-3">
                            <NxButton variant="copper" size="sm" onClick={markHelpful} icon={<ThumbsUp size={14} />} data-testid="btn-helpful">
                                {pt.kb.helpful} ({selected.helpful_count || 0})
                            </NxButton>
                        </div>
                    </GlassCard>
                </motion.div>
            </div>
        );
    }

    return (
        <div data-testid="kb-page" className="space-y-6">
            <PageHeader
                title={pt.kb.title}
                subtitle={pt.kb.subtitle}
                testId="kb-header"
            />

            <GlassCard padding="p-4">
                <div className="relative max-w-2xl">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--slate-mid)]" />
                    <GlassInput
                        placeholder={pt.kb.searchPlaceholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="kb-search-input"
                    />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="kb-category-filters">
                    <button
                        onClick={() => setActiveCategory("")}
                        className={"px-3 py-1 rounded-full text-xs font-body transition-all " + (activeCategory === "" ? "bg-[color:var(--copper)] text-white" : "bg-[color:var(--bg-surface)] text-[color:var(--slate-deep)] border border-[color:var(--glass-border)] hover:border-[color:var(--copper)]")}
                        data-testid="kb-cat-all"
                    >
                        {pt.kb.filterAll}
                    </button>
                    {Object.entries(pt.kb.categories).map(([k, label]) => {
                        const count = categories.find((c) => c.category === k)?.count || 0;
                        if (count === 0) return null;
                        return (
                            <button
                                key={k}
                                onClick={() => setActiveCategory(k)}
                                className={"px-3 py-1 rounded-full text-xs font-body transition-all flex items-center gap-1.5 " + (activeCategory === k ? "bg-[color:var(--copper)] text-white" : "bg-[color:var(--bg-surface)] text-[color:var(--slate-deep)] border border-[color:var(--glass-border)] hover:border-[color:var(--copper)]")}
                                data-testid={`kb-cat-${k}`}
                            >
                                {label} <span className="font-mono opacity-70">{count}</span>
                            </button>
                        );
                    })}
                </div>
            </GlassCard>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-40" />)}
                </div>
            ) : articles.length === 0 ? (
                <GlassCard padding="p-12" className="text-center">
                    <BookOpen size={32} className="mx-auto mb-3 text-[color:var(--slate-light)]" />
                    <p className="font-body text-sm text-[color:var(--slate-mid)]">{pt.kb.empty}</p>
                </GlassCard>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kb-grid">
                    {articles.map((a, idx) => (
                        <motion.button
                            key={a.id}
                            type="button"
                            onClick={() => openArticle(a.id)}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: Math.min(idx * 0.04, 0.4) }}
                            className="text-left"
                            data-testid={`kb-card-${a.id}`}
                        >
                            <GlassCard hover padding="p-5" className="h-full flex flex-col">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-wide" style={{ background: `${CATEGORY_COLOR[a.category]}22`, color: CATEGORY_COLOR[a.category] }}>
                                        {pt.kb.categories[a.category] || a.category}
                                    </span>
                                </div>
                                <h3 className="font-display text-lg text-[color:var(--slate-deep)] leading-tight tracking-tight mb-2">
                                    {a.title}
                                </h3>
                                <p className="font-body text-sm text-[color:var(--slate-mid)] leading-relaxed flex-1 line-clamp-3">
                                    {a.summary}
                                </p>
                                <div className="mt-4 pt-3 border-t border-[color:var(--glass-border)] flex items-center justify-between">
                                    <span className="font-mono text-[10px] text-[color:var(--slate-mid)] flex items-center gap-2">
                                        <span className="flex items-center gap-1"><Eye size={11} />{a.views}</span>
                                        <span className="flex items-center gap-1"><ThumbsUp size={11} />{a.helpful_count}</span>
                                    </span>
                                    <span className="text-[color:var(--copper)]">
                                        <ChevronRight size={16} />
                                    </span>
                                </div>
                            </GlassCard>
                        </motion.button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default KBPage;
